import { CrossBlackIcon } from "@/src/Asset";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import SearchBar from "./SearchBar";
import { StreamFilterButtons } from "./FilterButtons";

interface StreamSectionProps {
  readonly setSelectedFilterCategory: (value: any) => void;
  readonly availableStreams: any[];
  readonly filterOptions: any;
  readonly filterSecStreamData: any[];
  readonly handleModalToggle: any;
}

export default function StreamSection({
  setSelectedFilterCategory,
  availableStreams,
  filterOptions,
  filterSecStreamData,
  handleModalToggle,
}: StreamSectionProps) {
  const [filterAvailableStreams, setFilterAvailableStreams] = useState<
    string[] | null
  >(null);

  useEffect(() => {
    if (filterOptions?.course?.id) {
      const filterStreamsByCourseId = filterSecStreamData
        .filter(
          (stream) =>
            Array.isArray(stream.courses) &&
            stream.courses.some(
              (course: any) => course.id === filterOptions?.course?.id
            )
        )
        .map((data) => data.id);

      // console.log("filterStreamsByCourseId ====> ", filterStreamsByCourseId);

      // Convert to Set for quick lookup
      const streamIdSet = new Set(filterStreamsByCourseId);

      // Filter the available streams
      const filteredStreams = availableStreams.filter((stream) =>
        streamIdSet.has(stream.id)
      );

      setFilterAvailableStreams(filteredStreams);
    }
    //  else {
    //   setFilterAvailableStreams(availableStreams);
    // }
  }, [availableStreams, filterSecStreamData, filterOptions]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredStreams, setFilteredStreams] = useState(availableStreams);
  const [noMatchData, setNoMatchData] = useState(false);

  // Prevent body scroll when popup is open
  // useEffect(() => {
  //   document.body.style.overflow = "hidden";
  //   return () => {
  //     document.body.style.overflow = "auto";
  //   };
  // }, []);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      setFilteredStreams(() => {
        const lowerCaseQuery = searchQuery.toLowerCase();

        // First, find states that match the query
        const matchingStates = availableStreams.filter((stream: any) =>
          stream.stream_name.toLowerCase().includes(lowerCaseQuery)
        );
        if (matchingStates?.length === 0) {
          setNoMatchData(true);
        } else {
          setNoMatchData(false);
        }

        // Then, find the remaining states that do not match
        const nonMatchingStates = availableStreams.filter(
          (stream: any) =>
            !stream.stream_name.toLowerCase().includes(lowerCaseQuery)
        );
        // Prioritize matching states first
        return [...matchingStates, ...nonMatchingStates];
      });
    } else {
      setFilteredStreams(availableStreams); // Reset if searchQuery is less than 3 chars
    }
  }, [searchQuery, availableStreams]);

  // Redirect state
  const [isRedirecting, setIsRedirecting] = useState(false);

  const router = useRouter();
  const handleUpdateStream = async (url: string) => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    await router.push(`${url}`);
    setIsRedirecting(false);
    const screenWidth = window.innerWidth;
    if (screenWidth >= 1024) {
      setSelectedFilterCategory(null);
    } else {
      handleModalToggle();
    }
  };

  return (
    <div>
      {/* mobile view: */}
      <div className="lg:hidden w-full h-fit">
        <div className="sticky top-0 bg-white">
          {/* Header */}
          <div className="text-lg font-bold text-[#334988] mb-2">Stream</div>

          {/* Search Bar */}
          <SearchBar
            placeholder="Find Stream"
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>

        {searchQuery.length >= 3 && noMatchData && (
          <p className="text-red-500 text-sm text-center mt-1">
            No Match Found
          </p>
        )}

        {/* scrollable content */}
        <div
          style={{
            overflow: "auto",
            msOverflowStyle: "none", // IE and Edge
            scrollbarWidth: "none",
          }}
          className="w-full max-h-fit sm:max-h-full overflow-x-auto overflow-y-auto flex flex-wrap sm:grid sm:grid-cols-2 gap-3 mt-4 mb-20"
        >
          <StreamFilterButtons
            filteredStreams={filteredStreams}
            filterOptions={filterOptions}
            filterAvailableStreams={filterAvailableStreams}
            isRedirecting={isRedirecting}
            handleUpdateStream={handleUpdateStream}
          />
        </div>
      </div>

      {/* desktop view: */}
      <div className="hidden lg:block absolute mt-3 left-[18%] !z-10">
        <div className="bg-white rounded-lg shadow-lg border w-[28rem] h-full overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white px-4 pt-4 pb-2">
            <div className="flex justify-between items-center mb-2">
              <div className="text-lg font-medium">Streams</div>
              <button
                onClick={() => setSelectedFilterCategory(null)}
                className="text-gray-600 hover:text-gray-900 text-2xl"
              >
                <CrossBlackIcon className="scale-75" />
              </button>
            </div>

            {/* Search Bar */}
            <SearchBar
              placeholder="Find Stream"
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>

          {searchQuery.length >= 3 && noMatchData && (
            <p className="text-red-500 text-sm text-center mt-1">
              No Match Found
            </p>
          )}

          {/* Scrollable Content */}
          <div className="flex flex-wrap gap-x-3 gap-y-4 justify-start px-4 pb-4">
            <StreamFilterButtons
              filteredStreams={filteredStreams}
              filterOptions={filterOptions}
              filterAvailableStreams={filterAvailableStreams}
              isRedirecting={isRedirecting}
              handleUpdateStream={handleUpdateStream}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
